import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Container, CalendarClock, Box, Pencil, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { ArrivalDayDialog } from "@/components/overview/ArrivalDayDialog";
import { ShipmentEditDialog } from "@/components/overview/ShipmentEditDialog";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const monthLabel = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" })
    : null;

/** Shipments grouped by shipment code: panel count, vehicle and estimated time of arrival. */
export function ShipmentSummary({
  shipments,
  projectId,
  pallets = [],
  canManage = false,
  highlight = null,
  onChanged,
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    if (!highlight || !shipments?.length) return undefined;
    setFlash(highlight);
    const node = document.querySelector(
      `[data-testid="shipment-card-${highlight}"]`
    );
    node?.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = setTimeout(() => setFlash(null), 2000);
    return () => clearTimeout(timer);
  }, [highlight, shipments]);

  const removeShipment = async (s) => {
    const label = `Delete shipment ${s.shipment_code || "(no code)"}? ${s.panels} panel(s) will go back to Not Shipped and lose their shipping record and pallet link.`;
    if (!window.confirm(label)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(
        `/projects/${projectId}/shipments/${encodeURIComponent(s.shipment_code || "__none__")}`
      );
      toast.success(`Shipment deleted · ${data.reverted} panel(s) reverted`);
      if (data.failed?.length)
        toast.error(`${data.failed.length} panel(s) could not be reverted`);
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  if (!shipments?.length) return null;
  return (
    <section className="mt-7" data-testid="shipment-summary-section">
      <div className="flex items-center gap-2">
        <Truck size={17} className="text-cyan-600" />
        <h2 className="font-display text-xl font-medium tracking-tight sm:text-2xl">Shipments</h2>
        <span className="mono rounded bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-600">
          {shipments.length} groups
        </span>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {shipments.map((s) => (
          <div
            key={`${s.shipment_code || "none"}-${s.shipping_method || "none"}`}
            data-testid={`shipment-card-${s.shipment_code || "no-code"}`}
            className={`rounded-xl border bg-white p-4 ${
              s.overdue ? "border-red-500/40" : "border-slate-200"
            } ${flash && flash === s.shipment_code ? "okn-blink-twice" : ""}`}
          >
            <div className="flex items-center gap-2">
              {s.shipping_method === "By Container" ? (
                <Container size={14} className="okn-wave text-cyan-600" />
              ) : (
                <Truck size={14} className="text-cyan-600" />
              )}
              <span className="mono text-[12.5px] text-slate-900">
                {s.shipment_code || "No shipment code"}
              </span>
              <span className="mono ml-auto text-[11px] text-slate-500">
                {s.shipping_method || "—"}
              </span>
              {canManage ? (
                <span className="flex items-center gap-0.5">
                  <button
                    data-testid={`shipment-edit-${s.shipment_code || "no-code"}`}
                    title="Edit shipment"
                    onClick={() => setEditing(s)}
                    className="grid h-7 w-7 place-items-center rounded text-slate-500 hover:bg-slate-100 hover:text-cyan-700"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    data-testid={`shipment-delete-${s.shipment_code || "no-code"}`}
                    title="Delete shipment"
                    disabled={busy}
                    onClick={() => removeShipment(s)}
                    className="grid h-7 w-7 place-items-center rounded text-slate-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={13} />
                  </button>
                </span>
              ) : null}
            </div>
            <p className="mono mt-3 text-2xl tracking-tight text-slate-900">{s.panels}</p>
            <p className="text-[11px] uppercase tracking-wider text-slate-500">panels</p>
            {s.shipment_code ? (
              <button
                data-testid={`shipment-show-in-model-${s.shipment_code}`}
                onClick={() =>
                  navigate(
                    `/projects/${projectId}/model?shipment_code=${encodeURIComponent(s.shipment_code)}&select=1`,
                  )
                }
                className="mono mt-2.5 flex items-center gap-1.5 rounded-full border border-cyan-500/40 bg-white px-3 py-1.5 text-[10px] uppercase tracking-wider text-cyan-700 transition-colors hover:bg-cyan-50"
              >
                <Box size={12} /> Show in model
              </button>
            ) : null}
            {s.pallets?.length ? (
              <div
                data-testid={`shipment-pallets-${s.shipment_code || "no-code"}`}
                className="mt-2 flex flex-wrap items-center gap-1"
              >
                <span className="mono text-[9.5px] uppercase tracking-wider text-slate-400">
                  {s.pallets.length} pallet{s.pallets.length > 1 ? "s" : ""}
                </span>
                {s.pallets.map((code) => (
                  <span
                    key={code}
                    className="mono rounded border border-slate-200 bg-[#EEF2F8] px-1.5 py-0.5 text-[9.5px] text-slate-700"
                  >
                    {code}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="mt-3 space-y-1 border-t border-slate-200 pt-2.5 text-[11.5px]">
              <div className="flex justify-between">
                <span className="text-slate-500">In transit / arrived</span>
                <span className="mono text-slate-700">
                  {s.in_transit} / {s.delivered}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Estimated time of arrival</span>
                <span className={`mono ${s.overdue ? "text-red-600" : "text-slate-700"}`}>
                  {s.estimated_arrival
                    ? new Date(s.estimated_arrival).toLocaleDateString()
                    : "—"}
                </span>
              </div>
              {s.days_left != null && s.in_transit > 0 ? (
                <p
                  className={`mono text-[10.5px] ${
                    s.overdue ? "text-red-600" : "text-slate-500"
                  }`}
                >
                  {s.overdue
                    ? `${Math.abs(s.days_left)} days overdue`
                    : `${s.days_left} days left`}
                </p>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      <ShipmentEditDialog
        projectId={projectId}
        shipment={editing}
        pallets={pallets}
        open={!!editing}
        onOpenChange={(next) => !next && setEditing(null)}
        onDone={onChanged}
      />
    </section>
  );
}

/** Four-week strip of upcoming panel arrivals (ETA), current week first. */
export function ArrivalCalendar({ calendar, projectId, onChanged, canConfirm = false }) {
  const [dayOpen, setDayOpen] = useState(null);
  const [overdueOpen, setOverdueOpen] = useState(false);
  if (!calendar?.days?.length) return null;
  const max = Math.max(...calendar.days.map((d) => d.panels), 1);
  const weeks = [];
  for (let i = 0; i < calendar.days.length; i += 7) weeks.push(calendar.days.slice(i, i + 7));

  return (
    <section className="mt-7" data-testid="arrival-calendar-section">
      <div className="flex items-center gap-2">
        <CalendarClock size={17} className="text-cyan-600" />
        <h2 className="font-display text-xl font-medium tracking-tight sm:text-2xl">
          Arrivals
        </h2>
        {calendar.weeks ? (
          <span className="mono rounded bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-600">
            {calendar.weeks} weeks
          </span>
        ) : null}
        {calendar.overdue_panels ? (
          <button
            data-testid="calendar-overdue-badge"
            onClick={() => setOverdueOpen(true)}
            title={canConfirm ? "Confirm overdue arrivals" : "View overdue shipments"}
            className="mono rounded bg-red-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-red-700 hover:bg-red-500/20"
          >
            {calendar.overdue_panels} overdue
          </button>
        ) : null}
      </div>
      <div className="okn-scroll mt-4 max-h-[70vh] overflow-auto rounded-xl border border-slate-200 bg-white p-4">
        <div className="min-w-0 space-y-2 sm:min-w-[640px]">
          <div className="hidden grid-cols-4 gap-2 sm:grid sm:grid-cols-7">
            {WEEKDAYS.map((day) => (
              <p
                key={day}
                className="mono text-[9.5px] uppercase tracking-[0.16em] text-slate-500"
              >
                {day}
              </p>
            ))}
          </div>
          {weeks.map((week, index) => (
            <div key={week[0].date}>
            {monthLabel(week[0].date) !== monthLabel(weeks[index - 1]?.[0].date) ? (
              <p className="mono mb-1 mt-2 text-[9px] uppercase tracking-[0.16em] text-slate-400">
                {monthLabel(week[0].date)}
              </p>
            ) : null}
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
              {week.map((day) => (
                <button
                  key={day.date}
                  data-testid={`calendar-day-${day.date}`}
                  title={
                    day.panels
                      ? `${day.panels} panels · ${day.shipment_codes.join(", ") || "no code"}${
                          day.received ? ` · ${day.received} arrived` : ""
                        }${canConfirm ? " — click to confirm delivery" : " — click for details"}`
                      : ""
                  }
                  disabled={!day.panels}
                  onClick={() => setDayOpen(day.date)}
                  className={`rounded-lg border px-2 py-2 text-left transition-colors ${
                    !day.panels
                      ? "cursor-default border-slate-100 bg-white"
                      : day.received === day.panels
                        ? "border-emerald-400 bg-emerald-500/10 hover:border-emerald-500"
                        : day.is_today
                          ? "border-cyan-500 bg-cyan-500/8"
                          : "border-slate-200 bg-slate-50 hover:border-cyan-400 hover:bg-cyan-500/5"
                  }`}
                >
                  <p className="mono text-[10px] text-slate-500">
                    {new Date(day.date).getDate()}
                  </p>
                  {day.panels ? (
                    <>
                      <p
                        className={`mono mt-1 text-[13px] ${
                          day.received === day.panels ? "text-emerald-700" : "text-slate-900"
                        }`}
                      >
                        {day.panels}
                      </p>
                      <div className="mt-1 flex h-1 overflow-hidden rounded-full bg-slate-200">
                        <div
                          className="h-full bg-emerald-500"
                          style={{ width: `${(day.received / max) * 100}%` }}
                        />
                        <div
                          className="h-full bg-cyan-500"
                          style={{ width: `${((day.panels - day.received) / max) * 100}%` }}
                        />
                      </div>
                      <p className="mono mt-1 truncate text-[9px] text-slate-500">
                        {day.received === day.panels
                          ? "arrived"
                          : day.shipment_codes[0] || day.methods[0] || ""}
                      </p>
                    </>
                  ) : (
                    <p className="mono mt-1 text-[13px] text-slate-300">—</p>
                  )}
                </button>
              ))}
            </div>
            </div>
          ))}
        </div>
      </div>
      <ArrivalDayDialog
        projectId={projectId}
        date={dayOpen}
        canConfirm={canConfirm}
        open={!!dayOpen}
        onOpenChange={(next) => !next && setDayOpen(null)}
        onDone={onChanged}
      />
      <ArrivalDayDialog
        projectId={projectId}
        overdue
        canConfirm={canConfirm}
        overdueBefore={calendar.start}
        open={overdueOpen}
        onOpenChange={setOverdueOpen}
        onDone={onChanged}
      />
    </section>
  );
}
